// src/data/demoDiaryPages.ts
// Demo diary pages shown to new users so the diary is not empty on first access.
// These are injected into local state only — never saved to Supabase.

import { DiaryPage } from '../components/DiaryContext';

const now = new Date().toISOString();

const makeBlock = (type: string, content: string) => ({ id: `b_${Math.random().toString(36).slice(2)}`, type, content });
const makeTradeBlock = (content: string, metadata: object) => ({ id: `b_${Math.random().toString(36).slice(2)}`, type: 'trade', content, metadata });

const blocks = (items: Array<{ type: string; content: string; metadata?: object }>) => JSON.stringify(items);

export const DEMO_DIARY_PAGES: DiaryPage[] = [
  // ── Research folder ──────────────────────────────────────────────────────────
  {
    id: 'demo_folder_studio',
    title: 'Research',
    type: 'folder',
    parentId: undefined,
    content: '[]',
    tags: [],
    order: 0,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_studio_1',
    title: 'Volume analysis — video 1',
    type: 'page',
    parentId: 'demo_folder_studio',
    content: blocks([
      makeBlock('h2', 'Volume analysis — video 1'),
      makeBlock('p', 'In this session I studied volumetric analysis applied to the Forex markets. I looked at how volume can confirm or invalidate a price setup.'),
      makeBlock('h3', 'Key points'),
      makeBlock('bullet', 'Volume Profile identifies high-liquidity zones'),
      makeBlock('bullet', 'POC (Point of Control) = the price with the highest volume'),
      makeBlock('bullet', 'VAH/VAL define 70% of the session\'s total volume'),
      makeBlock('p', 'To explore further: how to combine Volume Profile with support/resistance levels.'),
    ]),
    tags: ['study', 'volume', 'forex'],
    order: 0,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_studio_2',
    title: 'Volume analysis — video 2',
    type: 'page',
    parentId: 'demo_folder_studio',
    content: blocks([
      makeBlock('h2', 'Volume analysis — video 2'),
      makeBlock('p', 'Continuing my study of volumetric analysis. This session I focused on index markets (NAS100, SP500) and how volume analysis differs from Forex.'),
      makeBlock('h3', 'Key differences'),
      makeBlock('bullet', 'On centralized markets volume is more reliable'),
      makeBlock('bullet', 'Imbalance zones = areas where price moved quickly on low volume'),
      makeBlock('p', 'I tested a few setups on the NAS100 using these techniques. Early results are encouraging.'),
    ]),
    tags: ['study', 'volume', 'indices'],
    order: 1,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_studio_3',
    title: 'Volume analysis — video 3',
    type: 'page',
    parentId: 'demo_folder_studio',
    content: blocks([
      makeBlock('h2', 'Volume analysis — video 3'),
      makeBlock('p', 'Third study session. Focus on volume-based trading strategies: how to enter a position using Volume Profile as a guide.'),
      makeBlock('h3', 'Setups studied'),
      makeBlock('bullet', 'Retest of the VAH in an uptrend → Long'),
      makeBlock('bullet', 'Break of the POC on high volume → trend continuation'),
      makeBlock('bullet', 'Rejection of the VAL in a downtrend → Short'),
      makeBlock('p', 'Next step: manual backtest across 30 historical sessions.'),
    ]),
    tags: ['study', 'volume', 'strategy'],
    order: 2,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },

  // ── Journal folder ───────────────────────────────────────────────────────────
  {
    id: 'demo_folder_journal',
    title: 'Journal',
    type: 'folder',
    parentId: undefined,
    content: '[]',
    tags: [],
    order: 1,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_journal_mar12',
    title: 'March 12',
    type: 'page',
    parentId: 'demo_folder_journal',
    content: blocks([
      makeBlock('h2', 'Wednesday, March 12, 2025'),
      makeBlock('p', 'Morning session on EURUSD. Price opened above the previous session\'s POC — long setup on the retest of the level.'),
      makeBlock('h3', 'Trade of the day'),
      makeTradeBlock('EURUSD Long', {
        title: 'EURUSD Long',
        symbol: 'EURUSD',
        direction: 'LONG',
        sentiment: 'bullish',
        datetime: '2025-03-12T09:15',
        entryPrice: '1.0842',
        exitPrice: '1.0878',
        profit: '+36 pips',
        status: 'closed',
        preTradeConfidence: 8,
        postTradeConfidence: 9,
        strategyId: '',
        screenshot: '',
        notes: 'Retest of the previous session\'s POC. Waited for confirmation before entering. Good execution.',
      }),
      makeBlock('bullet', 'GBPUSD — no clean setup, skipped'),
      makeBlock('h3', 'Reflections'),
      makeBlock('p', 'Good patience today. I waited for confirmation before entering and didn\'t force the second trade. To improve: trailing stop management.'),
    ]),
    tags: ['journal', 'forex', 'eurusd'],
    order: 0,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_journal_mar13',
    title: 'March 13',
    type: 'page',
    parentId: 'demo_folder_journal',
    content: blocks([
      makeBlock('h2', 'Thursday, March 13, 2025'),
      makeBlock('p', 'Tough session. Macro news at 2:30pm (US CPI) generated unusual volatility. I decided to stay flat after the release.'),
      makeBlock('h3', 'Pre-session analysis'),
      makeBlock('bullet', 'Bearish bias on DXY → favors EURUSD long'),
      makeBlock('bullet', 'Key resistance at 1.0920 — to watch'),
      makeBlock('h3', 'Result'),
      makeBlock('p', 'No trades taken. An observation day. Better to stay out than force a trade in an uncertain context.'),
    ]),
    tags: ['journal', 'macro', 'discipline'],
    order: 1,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_journal_mar14',
    title: 'March 14',
    type: 'page',
    parentId: 'demo_folder_journal',
    content: blocks([
      makeBlock('h2', 'Friday, March 14, 2025'),
      makeBlock('p', 'European session. XAU/USD in a strong uptrend. I took advantage of a pullback to the $2,900 psychological level.'),
      makeBlock('h3', 'Trade'),
      makeTradeBlock('XAUUSD Long', {
        title: 'XAUUSD Long',
        symbol: 'XAUUSD',
        direction: 'LONG',
        sentiment: 'bullish',
        datetime: '2025-03-14T09:30',
        entryPrice: '2898',
        exitPrice: '2924',
        profit: '+$26/oz (1.6R)',
        status: 'closed',
        preTradeConfidence: 9,
        postTradeConfidence: 9,
        strategyId: '',
        screenshot: '',
        notes: 'Pullback to the $2,900 psychological level. Stop at $2,882, target $2,930. Main trend is long — no sense fighting it.',
      }),
      makeBlock('h3', 'Lesson of the week'),
      makeBlock('p', 'Following the main trend is far more profitable than hunting counter-trends. This week the long bias on gold was clear — going against it made no sense.'),
    ]),
    tags: ['journal', 'xauusd', 'trend'],
    order: 2,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
  {
    id: 'demo_journal_mar15',
    title: 'March 15',
    type: 'page',
    parentId: 'demo_folder_journal',
    content: blocks([
      makeBlock('h2', 'Saturday, March 15, 2025'),
      makeBlock('p', 'Weekly review day. I went through all of the week\'s trades and identified recurring patterns.'),
      makeBlock('h3', 'Weekly stats'),
      makeBlock('bullet', '3 trades total: 2 wins, 0 losses, 1 break-even'),
      makeBlock('bullet', 'Win rate: 67% — in line with the average'),
      makeBlock('bullet', 'Weekly profit factor: 2.4'),
      makeBlock('h3', 'Goals for next week'),
      makeBlock('bullet', 'Keep following the trend on metals'),
      makeBlock('bullet', 'Reduce size in the US session (too much volatility)'),
      makeBlock('p', 'Overall a good week. Discipline is improving.'),
    ]),
    tags: ['journal', 'review', 'weekly'],
    order: 3,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  },
];

export const DEMO_DIARY_REMOVED_KEY = (userId: string | null) =>
  `demo_diary_removed_${userId || 'anon'}`;
